/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Accordion,
  AccordionItem,
  FormGroup,
  TextArea,
  TextInput
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  setDraftBodyOptionValue,
  setDraftElasticFilterValue,
  setDraftElasticQueryBodyValue,
  setDraftTitleOptionValue,
  setDraftUrlOptionValue,
  setIsElasticConfigValid
} from '../../store/reducer';
import {
  selectDraftBodyOptionValue,
  selectDraftElasticFilterValue,
  selectDraftElasticQueryBodyValue,
  selectDraftTitleOptionValue,
  selectDraftUrlOptionValue
} from '../../store/selectors';
import { messages } from './messages';

import './ElasticSearchConfigFields.scss';

/**
 * A Component for setting elastic search fields - title, body and URL.
 */

/**
 * TODO ES: In the future we could fetch elastic search fields for an instance and use the configFields.jsx file,
 * instead of this file, to let the user choose which fields they would like to use from a comboBox.
 */
export const ElasticSearchConfigFields = () => {
  const dispatch = useDispatch();
  const formatMsg = useIntl().formatMessage;

  const title = useSelector(selectDraftTitleOptionValue);
  const [invalidTitle, setInvalidTitle] = useState(false);
  const [invalidTitleText, setInvalidTitleText] = useState('');

  const body = useSelector(selectDraftBodyOptionValue);
  const [invalidBody, setInvalidBody] = useState(false);
  const [invalidBodyText, setInvalidBodyText] = useState('');

  const url = useSelector(selectDraftUrlOptionValue);

  // The above properties are all strings, however the next property "filter" is an array, and the following property
  // "query body" is an object. These unique properties need to be parsed using JSON.parse before they are sent to
  // store, otherwise store would reject the payload if these values contained unparsed strings. This causes two side
  // effects: First, we need to stringify any previously saved values for filter and query body when this panel is first
  // open, that way we can show the string representation of the array and object within the appropriate text fields
  // which will enable the user to make changes. Second, we can't save any inprogress / invalid JSON in redux because
  // JSON.parse will fail trying to parse it, so instead we use a react state variable to keep track of the inprogress
  // inputs, that way those inprogress inputs can be passed back to the appropriate fields and shown to the user so they
  // can continue making changes. Once the user supplies valid JSON for the fields, then the validation errors will be
  // removed, and the save button will become enabled (assuming the other fields are also valid).
  const [filter, setFilter] = useState(
    JSON.stringify(useSelector(selectDraftElasticFilterValue))
  );
  const [invalidFilter, setInvalidFilter] = useState(false);
  const [invalidFilterText, setInvalidFilterText] = useState('');

  const [queryBody, setQueryBody] = useState(
    JSON.stringify(useSelector(selectDraftElasticQueryBodyValue))
  );
  const [invalidQueryBody, setInvalidQueryBody] = useState(false);
  const [invalidQueryBodyText, setInvalidQueryBodyText] = useState('');

  const titleChange = e => {
    const title = e?.target?.value ?? '';
    if (title === null || _.isEmpty(title?.trim())) {
      setInvalidTitle(true);
      setInvalidTitleText(formatMsg(messages.invalidEmptyValueText));
    } else {
      setInvalidTitle(false);
      setInvalidTitleText('');
    }
    dispatch(setDraftTitleOptionValue(title));
  };

  const bodyChange = e => {
    const body = e?.target?.value ?? '';
    if (body === null || _.isEmpty(body?.trim())) {
      setInvalidBody(true);
      setInvalidBodyText(formatMsg(messages.invalidEmptyValueText));
    } else {
      setInvalidBody(false);
      setInvalidBodyText('');
    }
    dispatch(setDraftBodyOptionValue(body));
  };

  const urlChange = e => {
    const url = e?.target?.value ?? '';
    // Url is optional so no validation needed to check that it exists.
    dispatch(setDraftUrlOptionValue(url));
  };

  const filterChange = e => {
    const filter = e?.target?.value ?? '';
    setFilter(filter);
    if (filter) {
      let filterArray;
      // Store expects an array of objects for the filter so we need to convert back and forth from a string to an
      // array.
      try {
        // JSON.parse thinks the json is invalid if there are any new line characters in the string. So first remove all
        // the white space and then try to transform the string to an array.
        filterArray = JSON.parse(filter.replace(/\s/g, ''));
      } catch (e) {
        console.log(e);
        // If the filter is not valid json then show a validation error.
        setInvalidFilter(true);
        setInvalidFilterText(formatMsg(messages.invalidJsonArrayValue));
        return;
      }

      // If filterArray is not an array, or does not contain only objects within the array, then show a validation error.
      if (
        !Array.isArray(filterArray) ||
        filterArray.filter(item => typeof item !== 'object').length > 0
      ) {
        setInvalidFilter(true);
        setInvalidFilterText(formatMsg(messages.invalidJsonArrayValue));
      } else {
        // If the filter is a valid json array then clear any validation errors and save the array.
        setInvalidFilter(false);
        setInvalidFilterText('');
        dispatch(setDraftElasticFilterValue(filterArray));
      }
    } else {
      // If the filter is blank then clear any validation errors and save the blank input.
      setInvalidFilter(false);
      setInvalidFilterText('');
      dispatch(setDraftElasticFilterValue(filter));
    }
  };

  const queryBodyChange = e => {
    const queryBody = e?.target?.value ?? '';
    setQueryBody(queryBody);
    if (queryBody) {
      let queryBodyObject;
      // Store expects an object for the query body so we need to convert back and forth from a string to an object.
      try {
        // JSON.parse thinks the json is invalid if there are any new line characters in the string. So first remove all
        // the white space and then try to transform the string to an object.
        queryBodyObject = JSON.parse(queryBody.replace(/\s/g, ''));
      } catch (e) {
        console.log(e);
        // If the queryBody is not valid json then show a validation error.
        setInvalidQueryBody(true);
        setInvalidQueryBodyText(formatMsg(messages.invalidJsonObjectValue));
        return;
      }

      // If queryBodyObject is not an object (or is an array) then show a validation error.
      if (
        typeof queryBodyObject !== 'object' ||
        Array.isArray(queryBodyObject)
      ) {
        setInvalidQueryBody(true);
        setInvalidQueryBodyText(formatMsg(messages.invalidJsonObjectValue));
      } else {
        // If the query body is a valid json object then clear any validation errors and save the object.
        setInvalidQueryBody(false);
        setInvalidQueryBodyText('');
        dispatch(setDraftElasticQueryBodyValue(queryBodyObject));
      }
    } else {
      // If the query body is blank then clear any validation errors and save the blank input.
      setInvalidQueryBody(false);
      setInvalidQueryBodyText('');
      dispatch(setDraftElasticQueryBodyValue(queryBody));
    }
  };

  // Update the parent panel on the validity of the inputs for the elastic instance.
  useEffect(() => {
    if (invalidBody || invalidTitle || invalidFilter || invalidQueryBody) {
      dispatch(setIsElasticConfigValid(false));
    } else {
      dispatch(setIsElasticConfigValid(true));
    }
  }, [dispatch, invalidBody, invalidFilter, invalidQueryBody, invalidTitle]);

  return (
    <>
      <p className="ElasticSearchConfigFields__instruction">
        {formatMsg(messages.configInstruction)}
      </p>
      <FormGroup legendText="" className="ElasticSearchConfigFields__form">
        <TextInput
          autoComplete="off"
          id="ElasticSearchConfigFields__form-title-input"
          invalid={invalidTitle}
          invalidText={invalidTitleText}
          placeholder={formatMsg(messages.fieldPlaceholder)}
          // TODO ES: Will need this to respect access control in the future.
          // readOnly={false}
          labelText={formatMsg(messages.fieldTitle)}
          onChange={titleChange}
          size="md"
          value={title}
          type="text"
        />
        <TextInput
          autoComplete="off"
          id="ElasticSearchConfigFields__form-body-input"
          invalid={invalidBody}
          invalidText={invalidBodyText}
          placeholder={formatMsg(messages.fieldPlaceholder)}
          // TODO ES: Will need this to respect access control in the future.
          // readOnly={false}
          labelText={formatMsg(messages.fieldBody)}
          onChange={bodyChange}
          size="md"
          value={body}
          type="text"
        />
        <TextInput
          autoComplete="off"
          id="ElasticSearchConfigFields__form-url-input"
          placeholder={formatMsg(messages.fieldPlaceholder)}
          // TODO ES: Will need this to respect access control in the future.
          // readOnly={false}
          labelText={formatMsg(messages.fieldUrl)}
          onChange={urlChange}
          size="md"
          value={url}
          type="text"
        />
        <Accordion align="start">
          <AccordionItem
            title={formatMsg(messages.dropdownAdvancedSettings, {
              name: 'Elasticsearch'
            })}
            className="ElasticSearchConfigFields__form-advanced-settings-accordion">
            <TextArea
              labelText={formatMsg(messages.fieldFilter)}
              rows={10}
              id="ElasticSearchConfigFields__form-filter-area"
              invalid={invalidFilter}
              invalidText={invalidFilterText}
              onChange={filterChange}
              value={filter}
            />
            <TextArea
              labelText={formatMsg(messages.fieldQueryBody)}
              rows={10}
              id="ElasticSearchConfigFields__form-query-body-area"
              invalid={invalidQueryBody}
              invalidText={invalidQueryBodyText}
              onChange={queryBodyChange}
              value={queryBody}
            />
          </AccordionItem>
        </Accordion>
      </FormGroup>
    </>
  );
};
